import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Heart, 
  User, 
  Bell, 
  SignOut, 
  House,
  List
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <Heart 
                weight="fill" 
                className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" 
              />
              <Heart 
                weight="fill" 
                className="w-5 h-5 text-amber-400 absolute -top-1 -right-1 group-hover:scale-110 transition-transform" 
              />
            </div>
            <div className="flex flex-col">
              <span className="script-font text-2xl text-primary leading-tight">
                Link, Laugh & Love
              </span>
              <span className="text-xs text-muted-foreground tracking-widest uppercase">
                Matchmaking
              </span>
            </div>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/') ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link 
              to="/bio-mission" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/bio-mission') ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid="nav-bio-mission"
            >
              Bio &amp; Mission
            </Link>
            <Link 
              to="/how-it-works" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/how-it-works') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              How It Works
            </Link>
            <Link 
              to="/pricing" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/pricing') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              Services
            </Link>
            <Link 
              to="/faq" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/faq') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              FAQ
            </Link>
            <Link 
              to="/safety" 
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive('/safety') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              Safety
            </Link>
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="w-5 h-5" />
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      <span className="hidden sm:inline">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <House className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/application')}>
                      <List className="w-4 h-4 mr-2" />
                      My Application
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <SignOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">Sign In</Button>
                </Link>
                <Link to="/signup">
                  <Button className="btn-primary" data-testid="signup-btn">
                    <Heart weight="fill" className="w-4 h-4 mr-2" />
                    Start Your Journey
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="navy-section text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative">
                <Heart weight="fill" className="w-8 h-8 text-primary" />
                <Heart weight="fill" className="w-5 h-5 text-amber-400 absolute -top-1 -right-1" />
              </div>
              <div>
                <span className="script-font text-2xl text-white block">Link, Laugh & Love</span>
                <span className="text-xs text-pink-200 tracking-widest uppercase">Matchmaking</span>
              </div>
            </div>
            <p className="text-pink-100/80 max-w-md leading-relaxed">
              Real Love Starts with a Real Connection. We connect intentional singles 
              through personality, values and real-life compatibility. Not endless swiping.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/how-it-works" className="text-pink-100/70 hover:text-white transition-colors">How It Works</Link></li>
              <li><Link to="/pricing" className="text-pink-100/70 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/faq" className="text-pink-100/70 hover:text-white transition-colors">FAQ</Link></li>
              <li><Link to="/safety" className="text-pink-100/70 hover:text-white transition-colors">Safety</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Contact</h4>
            <ul className="space-y-3 text-pink-100/70">
              <li><a href="mailto:LinkLaughLoveMatch@gmail.com" className="hover:text-white">LinkLaughLoveMatch@gmail.com</a></li>
              <li><a href="tel:8107991060" className="hover:text-white">(810) 799-1060</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Legal</h4>
            <ul className="space-y-3 text-pink-100/70">
              <li><Link to="/anti-stalking-policy" className="hover:text-white">Privacy & Anti-Stalking Policy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="hover:text-white">Refund Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="script-font text-xl text-pink-200 mb-2">Relationship is a Blessing</p>
          <p className="text-pink-100/50 text-sm">
            © {new Date().getFullYear()} Link, Laugh & Love Matchmaking. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export const PageLayout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};
